import UserAvatar from "./UserAvatar";
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './Sidebar.css';
import {
  FaTachometerAlt,
  FaClipboardList,
  FaInbox,
  FaEnvelope,
  FaUser,
  FaUsers,
  FaUniversity,
  FaBuilding,
  FaChartBar,
  FaSignOutAlt,
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

type Role =
  | 'APPLICANT'
  | 'DEPARTMENT'
  | 'FACULTY_RAP'
  | 'FACULTY_ADMIN'
  | 'INSTITUTE_REPRESENTATIVE'
  | 'ADMIN'
  | 'RESEARCH_CHAIR'
  | 'PROFESSOR'
  | 'RESEARCH_OFFICER'
  | 'ADMIN_CYBER'
  | 'REVIEWER';

interface SidebarProps {
  onSignOut: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  isCollapsed: boolean;
  isMobileOpen: boolean;
  onToggleSidebar: () => void;
}

type MenuItem = {
  key: string;
  label: string;
  to: string;
  icon: React.ReactNode;
  allowedFor: Role[] | 'ALL';
};

const Sidebar: React.FC<SidebarProps> = ({
  onSignOut,
  isCollapsed,
  isMobileOpen,
  onToggleSidebar,
}) => {
  const { account } = useAuth();
  const userType: Role | string = (account as any)?.userType || '';
  const location = useLocation();

  // Derive display name + initials for profile chip
  const rawFirstName =
    (account as any)?.firstName || (account as any)?.name || '';
  const rawLastName =
    (account as any)?.lastName || (account as any)?.surname || '';
  const fullName =
    [rawFirstName, rawLastName].filter(Boolean).join(' ') ||
    (account as any)?.fullName ||
    (account as any)?.username ||
    '';
  const displayName =
    (account as any)?.preferredName ||
    rawLastName ||
    rawFirstName ||
    fullName ||
    'User';

  const initials = useMemo<string>(() => {
    const base = fullName || displayName;
    if (!base) return 'U';
    const parts = base.trim().split(/\s+/);
    return parts
      .slice(0, 2)
      .map((p: string) => p.charAt(0).toUpperCase())
      .join('');
  }, [fullName, displayName]);

  // Responsive breakpoint: <768px is "mobile" mode (drawer)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    if (isMobile && isMobileOpen) {
      onToggleSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Esc to close on mobile
      if (isMobile && isMobileOpen && e.key === 'Escape') {
        onToggleSidebar();
      }
      // Ctrl/Cmd + B to toggle
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        onToggleSidebar();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, isMobileOpen, onToggleSidebar]);

  // Who sees everything
  const seeEverything: Role[] = ['RESEARCH_OFFICER', 'ADMIN_CYBER'];

  // Menu configuration
  const MENU: MenuItem[] = useMemo(
    () => [
      {
        key: 'dashboard',
        label: 'Dashboard',
        to: '/dashboard',
        icon: <FaTachometerAlt className="sidebar-icon" />,
        allowedFor: 'ALL',
      },
      {key:'my-applications',label:'My Applications',to:'/my-applications',icon:<FaClipboardList className="sidebar-icon"/>,allowedFor:'ALL'},
      {key:'clearances',label:'Clearance Registry',to:'/clearances',icon:<FaClipboardList className="sidebar-icon"/>,allowedFor:'ALL'},
      {key:'action-centre',label:'Action Centre',to:'/action-centre',icon:<FaClipboardList className="sidebar-icon"/>,allowedFor:'ALL'},
      {key:'committee-meetings',label:'Committee Meetings',to:'/committee-meetings',icon:<FaUsers className="sidebar-icon"/>,allowedFor:['DEPARTMENT','FACULTY_RAP','FACULTY_ADMIN','INSTITUTE_REPRESENTATIVE','RESEARCH_CHAIR','REVIEWER','PROFESSOR','ADMIN']},
      {
        key: 'applications',
        label: 'Applications',
        to: '/applications',
        icon: <FaClipboardList className="sidebar-icon" />,
        allowedFor: 'ALL',
      },
      {
        key: 'inbox',
        label: 'Inbox',
        to: '/approval-inbox',
        icon: <FaInbox className="sidebar-icon" />,
        allowedFor: [
          'DEPARTMENT',
          'FACULTY_RAP',
          'FACULTY_ADMIN',
          'INSTITUTE_REPRESENTATIVE',
          'ADMIN',
          'RESEARCH_CHAIR',
          'REVIEWER',
          'PROFESSOR',
          'RESEARCH_OFFICER',
          'ADMIN_CYBER',
        ],
      },
      {
        key: 'departments',
        label: 'Departments/Centres',
        to: '/departments',
        icon: <FaBuilding className="sidebar-icon" />,
        allowedFor: [
          'FACULTY_RAP',
          'FACULTY_ADMIN',
          'INSTITUTE_REPRESENTATIVE',
          'ADMIN',
          'RESEARCH_CHAIR',
          'RESEARCH_OFFICER',
          'ADMIN_CYBER',
          'REVIEWER',
        ],
      },
      {
        key: 'faculties',
        label: 'Faculties/Institutes',
        to: '/faculties',
        icon: <FaUniversity className="sidebar-icon" />,
        allowedFor: [
          'ADMIN',
          'RESEARCH_CHAIR',
          'RESEARCH_OFFICER',
          'ADMIN_CYBER',
          'REVIEWER',
        ],
      },
      {
        key: 'users',
        label: 'Users',
        to: '/user-management',
        icon: <FaUsers className="sidebar-icon" />,
        allowedFor: ['RESEARCH_OFFICER', 'ADMIN_CYBER'],
      },
      {
        key: 'workflow-config',
        label: 'Workflow Setup',
        to: '/workflow-configuration',
        icon: <FaClipboardList className="sidebar-icon" />,
        allowedFor: ['ADMIN', 'RESEARCH_OFFICER', 'ADMIN_CYBER'],
      },
      {
        key: 'system-logs',
        label: 'System Logs',
        to: '/system-logs',
        icon: <FaClipboardList className="sidebar-icon" />,
        allowedFor: ['ADMIN_CYBER'],
      },
      {
        key: 'messages',
        label: 'Messages',
        to: '/messages',
        icon: <FaEnvelope className="sidebar-icon" />,
        allowedFor: 'ALL',
      },
      {
        key: 'reports',
        label: 'Reports',
        to: '/reports',
        icon: <FaChartBar className="sidebar-icon" />,
        allowedFor: 'ALL',
      },
      {
        key: 'profile',
        label: 'My Profile',
        to: '/user-profile',
        icon: <FaUser className="sidebar-icon" />,
        allowedFor: 'ALL',
      },
    ],
    []
  );

  const canSee = (item: MenuItem, role: string) => {
    if (item.key === 'system-logs') return role === 'ADMIN_CYBER';
    if (item.allowedFor === 'ALL') return true;
    if (seeEverything.includes(role as Role)) return true;
    return (item.allowedFor as Role[]).includes(role as Role);
  };

  const filteredMenu = MENU.filter((item) => {

    return canSee(item, userType);
  });

  // Collapsed state helpers
  const showText = !isCollapsed || (isMobile && isMobileOpen);
  const sidebarClasses = [
    'sidebar',
    isCollapsed ? 'collapsed' : '',
    isMobile ? 'mobile' : '',
    isMobile && isMobileOpen ? 'open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {isMobile && isMobileOpen && (
        <div
          className="mobile-overlay"
          onClick={onToggleSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={sidebarClasses}
        role="navigation"
        aria-label="Primary"
      >
        <div className="sidebar-header">
          {((isMobile && isMobileOpen) || (!isMobile && !isCollapsed)) && (
            <div className="sidebar-logo">
              <img src="/logo2.png" alt="Logo" />
            </div>
          )}

          <button
            type="button"
            className="sidebar-toggle"
            aria-expanded={isMobile ? isMobileOpen : !isCollapsed}
            onClick={onToggleSidebar}
            aria-label={
              isMobile
                ? isMobileOpen
                  ? 'Close menu'
                  : 'Open menu'
                : isCollapsed
                ? 'Expand sidebar'
                : 'Collapse sidebar'
            }
          >
            <span className="sidebar-toggle-bar" />
            <span className="sidebar-toggle-bar" />
            <span className="sidebar-toggle-bar" />
          </button>
        </div>

        <div className="sidebar-separator" />

        <nav className="sidebar-nav">
          <ul>
            {filteredMenu.map((item) => (
              <li key={item.key} className="sidebar-item">
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    'sidebar-link' + (isActive ? ' active' : '')
                  }
                  aria-label={item.label}
                >
                  {item.icon}

                  {showText && (
                    <span className="sidebar-text">{item.label}</span>
                  )}

                  {!showText && (
                    <span className="sidebar-tooltip" role="tooltip">
                      {item.label}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          {/* Profile chip above logout */}
          <div className="sidebar-profile">
            {showText ? (
              <div
                className="sidebar-profile-card"
                title={fullName || displayName}
              >
                <div className="sidebar-profile-avatar">
                  <UserAvatar userId={(account as any)?.id} name={fullName || initials}/>
                </div>
                <div className="sidebar-profile-text">
                  <span className="sidebar-profile-welcome">Welcome</span>
                  <span className="sidebar-profile-name">{displayName}</span>
                </div>
              </div>
            ) : (
              <div
                className="sidebar-profile-avatar-only"
                title={fullName || displayName}
              >
                <UserAvatar userId={(account as any)?.id} name={fullName || initials}/>
              </div>
            )}
          </div>

          <a
            href="#signout"
            onClick={onSignOut}
            className="sidebar-link sidebar-signout"
            aria-label="Logout"
          >
            <FaSignOutAlt className="sidebar-icon" />
            {showText && <span className="sidebar-text">Logout</span>}
            {!showText && (
              <span className="sidebar-tooltip" role="tooltip">
                Logout
              </span>
            )}
          </a>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
